import React from "react";
import { usePermissions } from "../../hooks/usePermissions";
import NoAccessPage from "./NoAccessPage";
import { Box, CircularProgress } from "@mui/material";
import logger from "../../utils/logger";

const ProtectedRoute = ({ permission, anyPermissions, children }) => {
  const {
    permissions,
    loading,
    provisioning,
    hasCheckedOnce
  } = usePermissions();

  // Show loading only on initial load
  if (loading && !hasCheckedOnce) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  // Show NoAccessPage only if we've checked and user truly has no permissions
  const hasAnyPermission = permissions && permissions.length > 0;

  if (!hasAnyPermission && hasCheckedOnce && !provisioning) {
    logger.log("User has no permissions after check");
    return <NoAccessPage />;
  }

  // If user is in provisioning state (waiting for permissions to be assigned)
  if (provisioning) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
        <Box sx={{ ml: 2 }}>Setting up your access...</Box>
      </Box>
    );
  }

  // Normalize permissions
  const normalized = (permissions || []).map((p) =>
    typeof p === "string"
      ? p.trim().toLowerCase()
      : p.name?.trim().toLowerCase()
  );

  // If no permission requirements specified, allow access
  if (!permission && !anyPermissions) {
    return children;
  }

  // Required single permission
  if (permission) {
    const required = permission.trim().toLowerCase();
    if (!normalized.includes(required)) {
      logger.log(`Missing required permission: ${required}`, normalized);
      return <NoAccessPage />;
    }
  }

  // Requires any of list
  if (anyPermissions) {
    const requiredList = anyPermissions.map((p) => p.trim().toLowerCase());
    const allowed = requiredList.some((p) => normalized.includes(p));

    if (!allowed) {
      logger.log(`Missing any of required permissions:`, requiredList, normalized);
      return <NoAccessPage />;
    }
  }

  return children;
};

export default ProtectedRoute;